import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import FeedCardWrapper from './FeedCardWrapper'
import ImageLightbox from './ImageLightbox'
import TeamAutocomplete from './TeamAutocomplete'
import Avatar from '../ui/Avatar'
import { useUpdateHotTake, useDeleteHotTake, useHotTakeImageUpload, useHotTakeVideoUpload, useTeamsForSport, useToggleBookmark, useRemindHotTake } from '../../hooks/useHotTakes'
import { useSearchUsers } from '../../hooks/useInvitations'
import { useActiveSports } from '../../hooks/useGames'
import { useAuth } from '../../hooks/useAuth'
import { toast } from '../ui/Toast'
import RichContent from './RichContent'
import PollDisplay from './PollDisplay'
import LinkPreview from './LinkPreview'
import PostEmbed from './PostEmbed'
import { extractFirstUrl } from '../../lib/urlUtils'
import { useHlsSource, streamThumbnailUrl } from '../../lib/useHlsSource'
import { parseEmbedSource } from '../../lib/embedParser'

// Convert a Supabase storage public URL to a resized thumbnail URL.
// Only works for Supabase-hosted images; external URLs pass through unchanged.
function thumbUrl(url, width = 800) {
  if (!url || !url.includes('/storage/v1/object/public/')) return url
  return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?width=${width}&resize=contain&quality=75`
}
import PickResultCard from '../picks/PickResultCard'

// Module-level feed mute preference — resets when all FeedVideo instances unmount (navigation away)
let feedUnmuted = false
let mountedVideoCount = 0

// Renders the pick/parlay/prop embedded in a flex post
function FlexTargetCard({ hot_take }) {
  const pick = hot_take.flex_pick
  const parlay = hot_take.flex_parlay
  const prop = hot_take.flex_prop_pick

  if (pick) {
    return (
      <div className="mb-2">
        <PickResultCard pick={pick} game={pick.games} totalCounts={pick.totalCounts} />
      </div>
    )
  }

  if (parlay) {
    return (
      <div className="bg-bg-primary/50 border border-correct rounded-xl p-4 mb-2 parlay-win-glow">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-text-primary">{parlay.leg_count}-Leg Parlay</span>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full text-correct">Won</span>
          <span className="text-sm font-bold text-correct ml-auto">+{parlay.points_earned} pts</span>
        </div>
        <div className="space-y-1">
          {(parlay.parlay_legs || []).map((leg) => {
            const team = leg.picked_team === 'home' ? leg.games?.home_team : leg.games?.away_team
            return (
              <div key={leg.id} className="flex items-center justify-between text-xs bg-bg-secondary rounded px-2 py-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-text-muted uppercase">{leg.games?.sports?.name || ''}</span>
                  <span className="text-text-primary font-medium truncate">{team}</span>
                </div>
                <span className="text-correct font-bold">W</span>
              </div>
            )
          })}
        </div>
        <div className="text-center text-xs text-text-muted mt-2">Combined: {Number(parlay.combined_multiplier).toFixed(2)}x</div>
      </div>
    )
  }

  if (prop) {
    const player_prop = prop.player_props
    const game = player_prop?.games
    return (
      <div className="bg-bg-primary/50 border border-correct rounded-xl p-4 mb-2">
        <div className="text-xs text-text-muted uppercase tracking-wider mb-1">{game?.sports?.name || ''}</div>
        <div className="text-sm font-semibold text-text-primary mb-1">{player_prop?.player_name} — {prop.chosen_line ?? player_prop?.line} {player_prop?.market_label}</div>
        {game && (
          <div className="text-xs text-text-muted mb-2">{game.away_team} @ {game.home_team}</div>
        )}
        <div className="text-sm text-text-secondary">
          Picked: <span className="font-semibold text-text-primary capitalize">{prop.picked_side}</span>
          {player_prop?.actual_value != null && (
            <span className="text-text-muted ml-2">(actual: {player_prop.actual_value})</span>
          )}
        </div>
        <div className="text-base font-bold text-correct mt-2">+{prop.points_earned} pts</div>
      </div>
    )
  }

  return null
}

export function VideoProcessingPlaceholder({ streamUid }) {
  const queryClient = useQueryClient()
  // Client-driven readiness poll — fires every 8s while the placeholder is
  // mounted, hitting the server which does a live Cloudflare lookup and
  // flips stream_ready_at as soon as the asset is playable. Feed invalidates
  // immediately so the placeholder swaps to the real video without waiting
  // for the next feed refetch tick.
  useEffect(() => {
    if (!streamUid) return
    let cancelled = false
    async function tick() {
      try {
        const result = await api.post(`/stream/check-ready/${streamUid}`)
        if (cancelled) return
        if (result?.ready) {
          queryClient.invalidateQueries({ queryKey: ['connections', 'activity'] })
        }
      } catch { /* keep polling — cron is the fallback */ }
    }
    // First poll immediately so if Cloudflare already finished by the time
    // this card mounts (rare but possible), the flag flips on the spot.
    tick()
    const id = setInterval(tick, 8_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [streamUid, queryClient])

  return (
    <div className="relative mt-2 rounded-lg border border-text-primary/15 bg-text-primary/[0.04] p-6 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-text-primary">Video processing…</div>
        <div className="text-xs text-text-primary/70 mt-0.5">
          We'll publish this post to the feed once your video finishes transcoding. Usually just a few seconds.
        </div>
      </div>
    </div>
  )
}

// Bound the video box by its ASPECT RATIO rather than its pixel dimensions.
//
// Cloudflare serves adaptive bitrate, so hls.js opens on a low rendition and
// switches up once it has measured bandwidth. Sizing from the element's
// intrinsic videoWidth/videoHeight therefore made the box visibly GROW
// partway through playback. Aspect is constant across renditions; resolution
// is not.
//
// Capping maxWidth at (60vh * aspect) is the same constraint as a 60vh
// height cap, expressed on the axis the container can actually control — so
// a portrait clip lands ~405px wide on a 1200px viewport while a landscape
// one still fills the column and comes nowhere near the cap.
function videoBoxStyle(aspect) {
  if (!aspect) return undefined
  return { maxWidth: `calc(60vh * ${aspect})` }
}

function FeedVideo({ url }) {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const userPausedRef = useRef(false)
  const [muted, setMuted] = useState(!feedUnmuted)
  const [aspect, setAspect] = useState(null)
  const [showPlayIcon, setShowPlayIcon] = useState(false)
  // HLS attach — no-op for legacy .mp4 URLs, wires up hls.js for Cloudflare
  // Stream manifests on non-Safari browsers.
  useHlsSource(videoRef, url)

  // Track mount count to reset feedUnmuted on navigation away
  useEffect(() => {
    mountedVideoCount++
    if (feedUnmuted) setMuted(false)
    return () => {
      mountedVideoCount--
      if (mountedVideoCount === 0) feedUnmuted = false
    }
  }, [])

  // Listen for mute/unmute events from other feed videos
  useEffect(() => {
    function handleUnmute() { setMuted(false) }
    function handleMute() { setMuted(true) }
    window.addEventListener('feed-video-unmuted', handleUnmute)
    window.addEventListener('feed-video-muted', handleMute)
    return () => {
      window.removeEventListener('feed-video-unmuted', handleUnmute)
      window.removeEventListener('feed-video-muted', handleMute)
    }
  }, [])

  // Sync muted property imperatively (React doesn't reliably update video.muted via JSX)
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted
  }, [muted])

  // Intersection Observer — autoplay on scroll
  useEffect(() => {
    const video = videoRef.current
    const container = containerRef.current
    if (!video || !container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!userPausedRef.current) {
            video.play().catch(() => {})
          }
        } else {
          video.pause()
          userPausedRef.current = false
          setShowPlayIcon(false)
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const togglePlayPause = useCallback((e) => {
    e.stopPropagation()
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play().catch(() => {})
      userPausedRef.current = false
      setShowPlayIcon(false)
    } else {
      video.pause()
      userPausedRef.current = true
      setShowPlayIcon(true)
    }
  }, [])

  const toggleMute = useCallback((e) => {
    e.stopPropagation()
    setMuted((m) => {
      const newMuted = !m
      feedUnmuted = !newMuted
      window.dispatchEvent(new Event(newMuted ? 'feed-video-muted' : 'feed-video-unmuted'))
      return newMuted
    })
  }, [])

  const expandVideo = useCallback((e) => {
    e.stopPropagation()
    const video = videoRef.current
    if (!video) return
    if (video.requestFullscreen) {
      video.requestFullscreen()
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen()
    }
  }, [])

  return (
    <div ref={containerRef} className="relative mt-2 cursor-pointer w-full mx-auto" style={videoBoxStyle(aspect)} onClick={togglePlayPause}>
      <video
        ref={videoRef}
        muted={muted}
        playsInline
        loop
        preload="metadata"
        poster={streamThumbnailUrl(url) || undefined}
        onLoadedMetadata={(e) => {
          const { videoWidth: w, videoHeight: h } = e.currentTarget
          if (w && h) setAspect(w / h)
        }}
        style={aspect ? { aspectRatio: String(aspect) } : undefined}
        className="block w-full max-h-[60vh] rounded-lg"
      />
      {showPlayIcon && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/50 rounded-full p-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-black/60 rounded-full p-1.5" onClick={toggleMute}>
        {muted ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        )}
      </div>
      <div className="absolute bottom-2 right-2 bg-black/60 rounded-full p-1.5" onClick={expandVideo}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </svg>
      </div>
    </div>
  )
}

const MAX_CHARS = 2000
const TRUNCATE_AT = 500

function ExpandableContent({ text, hideEmbeddedUrl = false, hideUrl = null }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const needsTruncation = text.length > TRUNCATE_AT
  if (!needsTruncation || expanded) {
    return (
      <div>
        <RichContent text={text} hideEmbeddedUrl={hideEmbeddedUrl} hideUrl={hideUrl} className="text-sm text-text-primary leading-relaxed" />
        {needsTruncation && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(false) }}
            className="text-xs text-accent hover:text-accent-hover transition-colors mt-1"
          >
            Show less
          </button>
        )}
      </div>
    )
  }
  // Truncate to a word boundary near TRUNCATE_AT to avoid mid-word cuts.
  const slice = text.slice(0, TRUNCATE_AT)
  const lastSpace = slice.lastIndexOf(' ')
  const truncated = (lastSpace > TRUNCATE_AT * 0.7 ? slice.slice(0, lastSpace) : slice).trimEnd() + '…'
  return (
    <div>
      <RichContent text={truncated} hideEmbeddedUrl={hideEmbeddedUrl} hideUrl={hideUrl} className="text-sm text-text-primary leading-relaxed" />
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(true) }}
        className="text-xs text-accent hover:text-accent-hover transition-colors mt-1"
      >
        Show more
      </button>
    </div>
  )
}

const sportTabs = [
  { label: 'NBA', key: 'basketball_nba' },
  { label: 'NCAAB', key: 'basketball_ncaab' },
  { label: 'WNCAAB', key: 'basketball_wncaab' },
  { label: 'MLB', key: 'baseball_mlb' },
  { label: 'NFL', key: 'americanfootball_nfl' },
  { label: 'NHL', key: 'icehockey_nhl' },
  { label: 'MLS', key: 'soccer_usa_mls' },
  { label: 'WNBA', key: 'basketball_wnba' },
  { label: 'NCAAF', key: 'americanfootball_ncaaf' },
]

export default function HotTakeFeedCard({ item, reactions, onUserTap, isBookmarked, onBookmarkToggle }) {
  const { hot_take } = item
  const { session } = useAuth()
  const navigate = useNavigate()
  // Set by LinkPreview once it has actually put a card on screen. Until
  // then the raw URL stays in the post text, so a link whose metadata comes
  // back empty is never left with neither a link nor a card.
  const [renderedPreviewUrl, setRenderedPreviewUrl] = useState(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const allImages = hot_take.image_urls?.length ? hot_take.image_urls : hot_take.image_url ? [hot_take.image_url] : []
  const [editing, setEditing] = useState(false)
  const [reminded, setReminded] = useState(false)
  const [showRemindInput, setShowRemindInput] = useState(false)
  const [remindComment, setRemindComment] = useState('')
  const remindHotTake = useRemindHotTake()
  const [editContent, setEditContent] = useState('')
  const [editTeamTags, setEditTeamTags] = useState([])
  const [editSport, setEditSport] = useState(null)
  const [editTeamSearch, setEditTeamSearch] = useState('')
  const [editUserTags, setEditUserTags] = useState([])
  const [editMentionQuery, setEditMentionQuery] = useState('')
  const [existingImageUrl, setExistingImageUrl] = useState(null)
  // Preserve the full image_urls array across edits so multi-image posts
  // don't collapse to a single image when the user edits text without
  // re-uploading the images.
  const [existingImageUrls, setExistingImageUrls] = useState(null)
  const [existingVideoUrl, setExistingVideoUrl] = useState(null)
  // Embed edit state — editEmbedTouched flags whether the user has changed
  // the embed at all this session. Only send embed_source to the server on
  // save if touched; otherwise leave whatever's already stored alone.
  const [editEmbedInput, setEditEmbedInput] = useState('')
  const [showEditEmbedInput, setShowEditEmbedInput] = useState(false)
  const [editEmbedTouched, setEditEmbedTouched] = useState(false)
  const editParsedEmbed = useMemo(() => parseEmbedSource(editEmbedInput), [editEmbedInput])
  const updateHotTake = useUpdateHotTake()
  const deleteHotTake = useDeleteHotTake()
  const { uploading, previewUrl, selectImage, removeImage, uploadImage, hasImage } = useHotTakeImageUpload()
  const { uploading: videoUploading, previewUrl: videoPreviewUrl, selectVideo, removeVideo, uploadVideo, hasVideo } = useHotTakeVideoUpload()
  const fileInputRef = useRef(null)
  const editTextareaRef = useRef(null)

  const { data: activeSports } = useActiveSports()
  const { data: editTeams } = useTeamsForSport(editing ? editSport : null)
  const { data: editMentionResults } = useSearchUsers(editing ? editMentionQuery : '')

  const sortedSportTabs = useMemo(() => {
    if (!activeSports?.length) return sportTabs
    const activeKeys = new Set(activeSports.map((s) => s.key))
    return [...sportTabs].sort((a, b) => {
      const aActive = activeKeys.has(a.key) ? 0 : 1
      const bActive = activeKeys.has(b.key) ? 0 : 1
      return aActive - bActive
    })
  }, [activeSports])

  function startEditing() {
    setEditContent(hot_take.content)
    setEditTeamTags(hot_take.team_tags || [])
    setEditUserTags(hot_take.tagged_users || [])
    setEditMentionQuery('')
    setEditSport(null)
    setEditTeamSearch('')
    setExistingImageUrl(hot_take.image_url || null)
    setExistingImageUrls(hot_take.image_urls?.length ? hot_take.image_urls : (hot_take.image_url ? [hot_take.image_url] : null))
    setExistingVideoUrl(hot_take.video_url || null)
    // Prefill the embed field with the canonical stored URL so the user can
    // see what's already there. The parser recognizes it and shows a chip.
    setEditEmbedInput(hot_take.embed_url || '')
    setShowEditEmbedInput(!!hot_take.embed_url)
    setEditEmbedTouched(false)
    setEditing(true)
  }

  function cancelEditing() {
    setEditing(false)
    setEditContent('')
    setEditTeamTags([])
    setEditUserTags([])
    setEditMentionQuery('')
    setEditSport(null)
    setEditTeamSearch('')
    setExistingImageUrl(null)
    setExistingImageUrls(null)
    setExistingVideoUrl(null)
    setEditEmbedInput('')
    setShowEditEmbedInput(false)
    setEditEmbedTouched(false)
    removeImage()
    removeVideo()
  }

  async function handleSave() {
    const trimmed = editContent.trim()
    if (!trimmed || trimmed.length > MAX_CHARS) return

    let imageUrl = existingImageUrl
    // Default to whatever array the post already had — only overwrite if
    // the user actually uploaded a new batch. Without this, the server
    // collapses image_urls to a single-element array on every edit.
    let imageUrls = existingImageUrls || undefined
    if (hasImage) {
      const urls = await uploadImage()
      if (!urls && hasImage) return // upload failed
      // uploadImage returns an array — flatten to the singular field for the
      // server while also keeping the multi-image array.
      imageUrls = urls
      imageUrl = urls?.[0]
    }

    let videoUrl = existingVideoUrl
    if (hasVideo) {
      videoUrl = await uploadVideo()
      if (!videoUrl && hasVideo) return // upload failed
    }

    // Only include embed_source in the PATCH if the user actually touched
    // the embed field. Empty string clears the embed; parsed input sets it.
    const embedPatch = editEmbedTouched
      ? { embed_source: editEmbedInput.trim() || null }
      : {}
    updateHotTake.mutate(
      { id: hot_take.id, content: trimmed, team_tags: editTeamTags.length ? editTeamTags : undefined, image_url: imageUrl || undefined, image_urls: imageUrls, video_url: videoUrl || undefined, user_tags: editUserTags.length ? editUserTags.map((u) => u.id) : undefined, ...embedPatch },
      {
        onSuccess: () => {
          cancelEditing()
        },
        onError: (err) => {
          if (err.status === 403) {
            toast('Your posting privileges have been suspended.', 'error')
          } else {
            toast(err.message || 'Failed to update', 'error')
          }
        },
      }
    )
  }

  function handleRemoveImage() {
    setExistingImageUrl(null)
    setExistingImageUrls(null)
    removeImage()
  }

  function handleRemoveVideo() {
    setExistingVideoUrl(null)
    removeVideo()
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) { e.target.value = ''; return }
    if (file.type.startsWith('video/')) {
      setExistingImageUrl(null)
      setExistingImageUrls(null)
      removeImage()
      setExistingVideoUrl(null)
      selectVideo(file)
    } else {
      setExistingVideoUrl(null)
      removeVideo()
      setExistingImageUrl(null)
      setExistingImageUrls(null)
      selectImage(file)
    }
    e.target.value = ''
  }

  function handleEditContentChange(e) {
    const val = e.target.value
    const cursorPos = e.target.selectionStart
    setEditContent(val)

    const beforeCursor = val.slice(0, cursorPos)
    const mentionMatch = beforeCursor.match(/(^|\s)@(\w*)$/)
    if (mentionMatch) {
      setEditMentionQuery(mentionMatch[2].length >= 2 ? mentionMatch[2] : '')
    } else {
      setEditMentionQuery('')
    }
  }

  function handleEditMentionSelect(user) {
    if (editUserTags.length >= 3 || editUserTags.some((u) => u.id === user.id)) return
    const textarea = editTextareaRef.current
    const cursorPos = textarea?.selectionStart || editContent.length
    const beforeCursor = editContent.slice(0, cursorPos)
    const afterCursor = editContent.slice(cursorPos)
    const match = beforeCursor.match(/@(\w*)$/)
    if (match) {
      const prefix = beforeCursor.slice(0, beforeCursor.length - match[0].length)
      const inserted = `@${user.username} `
      const newContent = prefix + inserted + afterCursor
      const newPos = prefix.length + inserted.length
      setEditContent(newContent)
      setEditUserTags([...editUserTags, user])
      setEditMentionQuery('')
      setTimeout(() => {
        if (textarea) {
          textarea.selectionStart = newPos
          textarea.selectionEnd = newPos
          textarea.focus()
        }
      }, 0)
    }
  }

  const isOwnTake = item.userId === session?.user?.id

  async function handleRemindSubmit() {
    try {
      await remindHotTake.mutateAsync({ hotTakeId: hot_take.id, comment: remindComment.trim() || undefined })
      setReminded(true)
      setShowRemindInput(false)
      setRemindComment('')
      toast(isOwnTake ? 'Your take has been resurfaced to the feed' : `@${item.username} has been reminded about this take`, 'success')
    } catch (err) {
      if (err.status === 403) {
        toast('You must be connected to this user to remind them', 'error')
      } else {
        toast(err.message || 'Failed to remind', 'error')
      }
    }
  }

  const charCount = editContent.length
  const canSave = charCount > 0 && charCount <= MAX_CHARS && !updateHotTake.isPending && !uploading && !videoUploading
  const currentPreview = previewUrl || existingImageUrl
  const currentVideoPreview = videoPreviewUrl || existingVideoUrl

  return (
    <FeedCardWrapper
      item={item}
      borderColor={hot_take.post_type === 'flex' ? 'green' : hot_take.post_type === 'prediction' ? 'green' : hot_take.post_type === 'poll' ? 'orange' : 'purple'}
      targetType="hot_take"
      targetId={hot_take.id}
      reactions={reactions}
      onUserTap={onUserTap}
      commentCount={item.commentCount}
      onEdit={startEditing}
    >
      {editing ? (
        <div className="space-y-2">
          <div className="relative">
            <textarea
              ref={editTextareaRef}
              value={editContent}
              onChange={handleEditContentChange}
              maxLength={MAX_CHARS}
              rows={3}
              className="w-full bg-bg-secondary border border-text-primary/20 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted resize-none outline-none focus:border-accent/40"
              autoFocus
            />

            {/* @mention autocomplete dropdown */}
            {editMentionQuery.length >= 2 && editMentionResults?.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-bg-primary border border-text-primary/20 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {editMentionResults
                  .filter((u) => !editUserTags.some((t) => t.id === u.id))
                  .slice(0, 5)
                  .map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleEditMentionSelect(u)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-text-primary/5 transition-colors"
                    >
                      <Avatar user={u} size="xs" />
                      <div className="min-w-0">
                        <div className="text-xs text-text-primary truncate">{u.display_name || u.username}</div>
                        <div className="text-[10px] text-text-muted truncate">@{u.username}</div>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Image preview */}
          {currentPreview && (
            <div className="relative inline-block">
              <img
                src={currentPreview}
                alt="Preview"
                className="max-h-32 rounded-lg object-cover"
              />
              <button
                onClick={handleRemoveImage}
                className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/90 transition-colors"
              >
                ×
              </button>
            </div>
          )}

          {/* Video preview */}
          {currentVideoPreview && (
            <div className="relative inline-block">
              <video
                src={currentVideoPreview}
                controls
                className="max-h-48 rounded-lg"
              />
              <button
                onClick={handleRemoveVideo}
                className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/90 transition-colors"
              >
                ×
              </button>
            </div>
          )}

          {/* Embed edit — same paste/preview pattern as the composer */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowEditEmbedInput((v) => !v); if (!showEditEmbedInput) setEditEmbedTouched(true) }}
              className={`transition-colors p-1 ${showEditEmbedInput || editParsedEmbed ? 'text-accent' : 'text-text-muted hover:text-text-secondary'}`}
              title="Embed YouTube or X"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
            </button>
            {(showEditEmbedInput || editParsedEmbed) && (
              <span className="text-[10px] uppercase tracking-wider text-text-muted">Embed</span>
            )}
          </div>
          {(showEditEmbedInput || editParsedEmbed) && (
            <div className="space-y-2">
              {showEditEmbedInput && (
                <textarea
                  value={editEmbedInput}
                  onChange={(e) => { setEditEmbedInput(e.target.value); setEditEmbedTouched(true) }}
                  placeholder="Paste YouTube / X link or embed code…"
                  rows={3}
                  className="w-full bg-bg-secondary border border-text-primary/20 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent resize-y font-mono"
                />
              )}
              {editParsedEmbed && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent/10 border border-accent/40 text-xs">
                  <span className="font-semibold text-accent">
                    {editParsedEmbed.provider === 'youtube' ? 'YouTube video' : 'X post'}
                  </span>
                  <span className="text-text-muted truncate max-w-[160px]">#{editParsedEmbed.refId}</span>
                  <button
                    onClick={() => { setEditEmbedInput(''); setShowEditEmbedInput(false); setEditEmbedTouched(true) }}
                    className="text-text-muted hover:text-text-primary leading-none ml-1"
                    aria-label="Remove embed"
                  >
                    ×
                  </button>
                </div>
              )}
              {showEditEmbedInput && editEmbedInput.trim() && !editParsedEmbed && (
                <div className="text-xs text-yellow-500">
                  Doesn't look like a YouTube / X link or embed code — post will save without an embed.
                </div>
              )}
            </div>
          )}

          {/* Sport chips for editing */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {sortedSportTabs.map((sport) => (
              <button
                key={sport.key}
                onClick={() => {
                  if (editSport === sport.key) {
                    setEditSport(null)
                    setEditTeamSearch('')
                  } else {
                    setEditSport(sport.key)
                    setEditTeamSearch('')
                  }
                }}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors ${
                  editSport === sport.key
                    ? 'bg-accent text-white'
                    : 'bg-bg-secondary text-text-secondary hover:bg-border'
                }`}
              >
                {sport.label}
              </button>
            ))}
          </div>

          {/* Team tag pills */}
          {editTeamTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {editTeamTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-accent px-2 py-0.5 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => setEditTeamTags(editTeamTags.filter((t) => t !== tag))}
                    className="hover:text-white transition-colors leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* User tag pills (edit) */}
          {editUserTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {editUserTags.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-500/15 text-purple-400 px-2 py-0.5 rounded-full"
                >
                  <Avatar user={u} size="xs" />
                  @{u.username}
                  <button
                    onClick={() => setEditUserTags(editUserTags.filter((t) => t.id !== u.id))}
                    className="hover:text-white transition-colors leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {editSport && editTeamTags.length < 5 ? (
                <div className="w-28">
                  <TeamAutocomplete
                    teams={(editTeams || []).filter((t) => !editTeamTags.includes(t))}
                    onSelect={(t) => setEditTeamTags([...editTeamTags, t])}
                    inputValue={editTeamSearch}
                    onInputChange={setEditTeamSearch}
                    placeholder="Tag team..."
                  />
                </div>
              ) : !editSport && editTeamTags.length < 5 ? (
                <span className="text-[10px] text-text-muted">Pick a sport to tag</span>
              ) : null}

              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-text-muted hover:text-text-secondary transition-colors p-1"
                title="Upload image/video"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
                onChange={handleFileChange}
                className="hidden"
              />

              {charCount > 0 && (
                <span className={`text-xs flex-shrink-0 ${charCount > MAX_CHARS ? 'text-incorrect' : 'text-text-muted'}`}>
                  {charCount}/{MAX_CHARS}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  if (!window.confirm('Delete this post? This cannot be undone.')) return
                  try {
                    await deleteHotTake.mutateAsync(hot_take.id)
                    toast('Post deleted', 'info')
                  } catch (err) {
                    toast(err.message || 'Failed to delete', 'error')
                  }
                }}
                disabled={deleteHotTake.isPending}
                className="text-xs text-incorrect hover:text-incorrect/80 transition-colors disabled:opacity-50"
              >
                Delete
              </button>
              <button
                onClick={cancelEditing}
                className="text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="bg-accent text-white text-xs font-semibold px-4 py-1.5 rounded-lg disabled:opacity-50 transition-opacity"
              >
                {updateHotTake.isPending || uploading || videoUploading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Viral badge */}
          {item.viral && (
            <div className="mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-orange-400 px-2 py-0.5 rounded-full">
                {'\uD83D\uDD25'} {item.remindCount}+ reminds
              </span>
            </div>
          )}

          {/* Post type badge */}
          {hot_take.post_type === 'prediction' && (
            <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full text-green-400 mb-1">Prediction</span>
          )}
          {hot_take.post_type === 'poll' && (
            <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full text-orange-400 mb-1">Poll</span>
          )}
          {hot_take.post_type === 'flex' && (
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full text-accent mb-1">
              <img src="/flex-button.png" alt="" className="w-3 h-3 object-contain" />
              Flex
            </span>
          )}

          {/* Flex comment (white bold, appears ABOVE the pick card) */}
          {hot_take.post_type === 'flex' && hot_take.content && (
            <div className="text-base font-bold text-white leading-relaxed mb-3">
              <RichContent text={hot_take.content} hideEmbeddedUrl hideUrl={renderedPreviewUrl} />
            </div>
          )}

          {/* Flex pick/parlay/prop card */}
          {hot_take.post_type === 'flex' && (
            <FlexTargetCard hot_take={hot_take} />
          )}

          {/* Content (non-flex posts) */}
          {hot_take.post_type !== 'flex' && (
            <ExpandableContent text={hot_take.content} hideEmbeddedUrl hideUrl={renderedPreviewUrl} />
          )}

          {/* Poll options */}
          {hot_take.post_type === 'poll' && (
            <PollDisplay hotTakeId={hot_take.id} />
          )}

          {/* Images */}
          {allImages.length > 0 && (
            <div className="relative mt-2 w-fit max-w-full mx-auto">
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(carouselIndex); setLightboxOpen(true) }}
                className="block"
              >
                {/* Capped like the video: a tall portrait photo at full
                    column width ran past the bottom of a desktop screen.
                    max-h bounds the height, max-w-full still bounds a wide
                    one, and the container hugs whichever wins so the
                    carousel arrows stay on the image rather than floating
                    out in the margin beside it. */}
                <img
                  src={thumbUrl(allImages[carouselIndex])}
                  alt=""
                  loading="lazy"
                  className="block max-w-full max-h-[60vh] rounded-lg"
                />
              </button>
              {allImages.length > 1 && (
                <>
                  {carouselIndex > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCarouselIndex((i) => i - 1) }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    >
                      &#8249;
                    </button>
                  )}
                  {carouselIndex < allImages.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCarouselIndex((i) => i + 1) }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    >
                      &#8250;
                    </button>
                  )}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {allImages.map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${i === carouselIndex ? 'bg-white' : 'bg-white/40'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Video — placeholder while Cloudflare Stream transcodes. The
              server hides pending posts from every viewer except the author,
              so only the uploader ever sees this state. */}
          {hot_take.stream_video_uid && !hot_take.stream_ready_at ? (
            <VideoProcessingPlaceholder streamUid={hot_take.stream_video_uid} />
          ) : (
            hot_take.video_url && <FeedVideo url={hot_take.video_url} />
          )}

          {/* YouTube / X embed — rendered from the safe (provider, ref_id)
              tuple the server extracted at submit time */}
          {hot_take.embed_provider && hot_take.embed_ref_id && (
            <PostEmbed provider={hot_take.embed_provider} refId={hot_take.embed_ref_id} />
          )}

          {/* Link preview — suppress when we have a structured embed so
              we don't show the same YouTube video twice */}
          {!hot_take.embed_provider && extractFirstUrl(hot_take.content) && (
            <LinkPreview
              url={extractFirstUrl(hot_take.content)}
              onRendered={setRenderedPreviewUrl}
            />
          )}

          {/* Team tags + user tags + remind + bookmark */}
          <div className="mt-2 flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {hot_take.team_tags?.length > 0 && hot_take.team_tags.map((tag) => (
                <button
                  key={tag}
                  onClick={(e) => { e.stopPropagation(); navigate(`/hub?tab=team_feed&team=${encodeURIComponent(tag)}`) }}
                  className="text-[10px] font-semibold uppercase tracking-wider text-accent px-2 py-0.5 rounded-full hover:bg-accent/10 transition-colors"
                >
                  {tag}
                </button>
              ))}
              {hot_take.tagged_users?.length > 0 && hot_take.tagged_users.map((u) => (
                <button
                  key={u.id}
                  onClick={(e) => { e.stopPropagation(); onUserTap?.(u.id) }}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-500/15 text-purple-400 px-2 py-0.5 rounded-full hover:bg-purple-500/25 transition-colors"
                >
                  <Avatar user={u} size="xs" />
                  @{u.username}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {!reminded && !showRemindInput && hot_take.post_type === 'prediction' && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRemindInput(true) }}
                  className="text-xs font-semibold px-2.5 py-1 rounded-full border border-text-primary/30 text-text-primary hover:border-text-primary/50 transition-colors"
                >
                  {isOwnTake ? 'Called It' : 'Remind'}
                </button>
              )}
              {onBookmarkToggle && (
                <button
                  onClick={(e) => { e.stopPropagation(); onBookmarkToggle(hot_take.id) }}
                  className={`p-1 transition-colors ${isBookmarked ? 'text-accent' : 'text-text-muted hover:text-text-secondary'}`}
                  title={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Inline remind input */}
          {showRemindInput && (
            <div className="mt-2 flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
              <textarea
                value={remindComment}
                onChange={(e) => {
                  setRemindComment(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${e.target.scrollHeight}px`
                }}
                placeholder="Add a comment (optional)"
                maxLength={280}
                rows={1}
                className="flex-1 min-w-0 bg-bg-secondary border border-text-primary/20 rounded-lg px-3 py-1.5 text-sm text-text-primary placeholder-text-muted outline-none focus:border-accent/40 resize-none overflow-hidden"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleRemindSubmit()
                  }
                }}
              />
              <button
                onClick={handleRemindSubmit}
                disabled={remindHotTake.isPending}
                className="shrink-0 bg-accent text-white text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 transition-opacity"
              >
                {remindHotTake.isPending ? '...' : 'Send'}
              </button>
              <button
                onClick={() => { setShowRemindInput(false); setRemindComment('') }}
                className="shrink-0 text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Lightbox */}
          {lightboxOpen && allImages.length > 0 && (
            <ImageLightbox
              images={allImages}
              initialIndex={lightboxIndex}
              onClose={() => setLightboxOpen(false)}
            />
          )}
        </>
      )}
    </FeedCardWrapper>
  )
}
